import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Check,
  ChevronDown,
  ChevronUp,
  Clock,
  Loader2,
  MessageCircle,
  Moon,
  Plus,
  Zap
} from "lucide-react";
import { Button } from "../../components/tailwind/button";
import { Card, CardContent } from "../../components/tailwind/card";
import { Input } from "../../components/tailwind/input";
import { Textarea } from "../../components/tailwind/textarea";
import { Switch } from "../../components/ui/switch";
import {
  saveBasicAutomationsRequest
} from "../../store/slices/workflow";
import { workflowTheme } from "./utils/workflowBuilderTheme";
import { whatsappPageShell, waShell } from "./utils/whatsappPageShell";
import { WaBone, WaCardBone } from "./components/skeletons/WhatsAppPageSkeletons";
import {
  BASIC_AUTOMATION_META,
  buildSaveBasicAutomationsPayload,
  buildScheduleFromWorkingHours,
  createDefaultRules,
  DAY_LABELS,
  getRuleMessageText,
  hasSavedBasicAutomations,
  normalizeBasicAutomationRules,
  setRuleMessageText,
  syncWorkingHoursState
} from "./utils/basicAutomationsUtils";

const RULE_ICONS = {
  WELCOME: MessageCircle,
  AWAY: Moon
};

export function BasicAutomationsPanel({ embedded = false, initialCreate = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { rules: apiRules, loading, error, saveLoading } = useSelector(
    (state) => state.workflowBasicAutomations || {}
  );

  const [rules, setRules] = useState(createDefaultRules);
  const [isDrafting, setIsDrafting] = useState(() => initialCreate || !!location.state?.create);
  const [expandedCard, setExpandedCard] = useState(null);
  const [showHours, setShowHours] = useState(false);
  const [workingDays, setWorkingDays] = useState(["Mon", "Tue", "Wed", "Thu", "Fri"]);
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("18:00");

  const hasSavedConfig = apiRules !== null && hasSavedBasicAutomations(apiRules);
  const showEditor = hasSavedConfig || isDrafting;

  useEffect(() => {
    if (apiRules === null) return;

    if (hasSavedBasicAutomations(apiRules)) {
      const normalized = normalizeBasicAutomationRules(apiRules);
      setIsDrafting(false);
      setRules(normalized);
      syncWorkingHoursState(normalized, setWorkingDays, setStartTime, setEndTime);
      return;
    }

    if (isDrafting || initialCreate || location.state?.create) {
      const defaults = createDefaultRules();
      setRules(defaults);
      syncWorkingHoursState(defaults, setWorkingDays, setStartTime, setEndTime);
    }
  }, [apiRules, initialCreate, isDrafting, location.state?.create]);

  const handleCreateFromScratch = () => {
    const defaults = createDefaultRules();
    setRules(defaults);
    syncWorkingHoursState(defaults, setWorkingDays, setStartTime, setEndTime);
    setExpandedCard(null);
    setIsDrafting(true);
  };

  const updateRuleByType = (type, updater) => {
    setRules((prev) =>
      prev.map((rule) => (rule.type === type ? updater(rule) : rule))
    );
  };

  const toggleAutomation = (type) => {
    updateRuleByType(type, (rule) => ({ ...rule, enabled: !rule.enabled }));
  };

  const updateMessage = (type, text) => {
    updateRuleByType(type, (rule) => setRuleMessageText(rule, text));
  };

  const toggleDay = (day) => {
    const next = workingDays.includes(day)
      ? workingDays.filter((d) => d !== day)
      : [...workingDays, day];
    setWorkingDays(next);
    const schedule = buildScheduleFromWorkingHours({ days: next, startTime, endTime });
    updateRuleByType("AWAY", (rule) => ({
      ...rule,
      config: { ...rule.config, schedule }
    }));
  };

  const handleStartTimeChange = (value) => {
    setStartTime(value);
    const schedule = buildScheduleFromWorkingHours({ days: workingDays, startTime: value, endTime });
    updateRuleByType("AWAY", (rule) => ({
      ...rule,
      config: { ...rule.config, schedule }
    }));
  };

  const handleEndTimeChange = (value) => {
    setEndTime(value);
    const schedule = buildScheduleFromWorkingHours({ days: workingDays, startTime, endTime: value });
    updateRuleByType("AWAY", (rule) => ({
      ...rule,
      config: { ...rule.config, schedule }
    }));
  };

  const handleSave = () => {
    dispatch(saveBasicAutomationsRequest(buildSaveBasicAutomationsPayload(rules)));
  };

  const saveButton = showEditor ? (
    <Button
      className="bg-[hsl(158,48%,36%)] hover:bg-[hsl(158,48%,30%)] text-white font-semibold shrink-0"
      onClick={handleSave}
      disabled={saveLoading || loading}
    >
      {saveLoading ? (
        <>
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          Saving…
        </>
      ) : (
        hasSavedConfig ? "Save changes" : "Save automations"
      )}
    </Button>
  ) : null;

  const panelContent = (
    <div className="space-y-6 animate-fade-in-up">
        <div className="flex items-start justify-between gap-4">
          {embedded ? (
            <p className={`text-sm ${workflowTheme.textSecondary}`}>
              Simple, rules-based responses that run without the visual canvas.
            </p>
          ) : (
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className={`h-9 w-9 shrink-0 ${workflowTheme.backIconButton}`}
                onClick={() => navigate("/whatsapp/workflows")}
                aria-label="Back to workflows"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </div>
          )}
          {saveButton}
        </div>

        {loading && (
          <div className="space-y-4" aria-busy="true" aria-label="Loading automations">
            <WaCardBone className="space-y-4">
              <WaBone className="h-5 w-48" />
              <WaBone className="h-10 w-full rounded-lg" />
              <WaBone className="h-24 w-full rounded-lg" />
              <WaBone className="h-10 w-32 rounded-lg ml-auto" />
            </WaCardBone>
          </div>
        )}
        {!loading && error && (
          <div className="text-sm text-red-600 dark:text-red-400">{error}</div>
        )}

        {!loading && !showEditor && (
          <Card className={`${workflowTheme.card} shadow-sm rounded-xl overflow-hidden`}>
            <CardContent className="p-10 flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-full bg-[#E1F5EE] dark:bg-emerald-950/50 flex items-center justify-center border border-[#9FE1CB] dark:border-emerald-800 mb-5">
                <Zap className="w-8 h-8 text-[#0F6E56] dark:text-emerald-400" />
              </div>
              <h2 className={`text-xl font-bold ${workflowTheme.textPrimary} mb-2`}>
                No basic automations yet
              </h2>
              <p className={`text-sm ${workflowTheme.textSecondary} max-w-md mb-6`}>
                Set up welcome and away messages without building a visual workflow.
                Start from a ready-made template and customize the rules to fit your business.
              </p>
              <Button
                className="bg-[hsl(158,48%,36%)] hover:bg-[hsl(158,48%,30%)] text-white font-semibold"
                onClick={handleCreateFromScratch}
              >
                <Plus className="w-4 h-4 mr-2" />
                Create from scratch
              </Button>
            </CardContent>
          </Card>
        )}

        {!loading && showEditor && (
          <>
            {isDrafting && !hasSavedConfig && (
              <div className={`${workflowTheme.aiBanner} rounded-lg px-4 py-3 text-sm`}>
                You are setting up basic automations for the first time. Toggle the rules you want,
                edit messages, then save to activate.
              </div>
            )}

            <Card className={`${workflowTheme.card} shadow-sm overflow-hidden`}>
              <button
                type="button"
                className="w-full p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors"
                onClick={() => setShowHours(!showHours)}
              >
                <div className="flex items-center gap-3 text-left">
                  <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-950/50 flex items-center justify-center border border-blue-100 dark:border-blue-900/50">
                    <Clock className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h3 className={`font-semibold text-sm ${workflowTheme.textPrimary}`}>
                      Working Hours Setup
                    </h3>
                    <p className={`text-xs ${workflowTheme.textSecondary}`}>
                      Active days and times for the away message.
                    </p>
                  </div>
                </div>
                {showHours ? (
                  <ChevronUp className={`w-5 h-5 ${workflowTheme.textMuted}`} />
                ) : (
                  <ChevronDown className={`w-5 h-5 ${workflowTheme.textMuted}`} />
                )}
              </button>

              {showHours && (
                <CardContent className={`pt-0 pb-5 border-t ${workflowTheme.borderSubtle} px-5`}>
                  <div className="mt-4 space-y-5">
                    <div>
                      <label className={`text-xs font-bold ${workflowTheme.textPrimary} uppercase tracking-wider mb-2 block`}>
                        Active Days
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {DAY_LABELS.map((day) => (
                          <button
                            key={day}
                            type="button"
                            onClick={() => toggleDay(day)}
                            className={`w-10 h-10 rounded-full text-xs font-medium transition-colors flex items-center justify-center ${
                              workingDays.includes(day)
                                ? "bg-[hsl(158,48%,36%)] text-white"
                                : "bg-gray-100 dark:bg-slate-800 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-slate-700"
                            }`}
                          >
                            {day}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-6">
                      <div className="flex-1">
                        <label className={`text-xs font-bold ${workflowTheme.textPrimary} uppercase tracking-wider mb-2 block`}>
                          Start Time
                        </label>
                        <Input
                          type="time"
                          value={startTime}
                          onChange={(e) => handleStartTimeChange(e.target.value)}
                          className={`max-w-[200px] ${workflowTheme.input}`}
                        />
                      </div>
                      <div className="flex-1">
                        <label className={`text-xs font-bold ${workflowTheme.textPrimary} uppercase tracking-wider mb-2 block`}>
                          End Time
                        </label>
                        <Input
                          type="time"
                          value={endTime}
                          onChange={(e) => handleEndTimeChange(e.target.value)}
                          className={`max-w-[200px] ${workflowTheme.input}`}
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>

            <div className="space-y-4">
              {BASIC_AUTOMATION_META.map((meta) => {
                const rule = rules.find((item) => item.type === meta.type);
                if (!rule) return null;

                const Icon = RULE_ICONS[meta.type];
                const isExpanded = expandedCard === meta.type;
                const message = getRuleMessageText(rule);

                return (
                  <Card
                    key={meta.type}
                    className={`${workflowTheme.card} overflow-hidden transition-all ${
                      rule.enabled ? "border-l-4 border-l-[#0F6E56] dark:border-l-emerald-500" : ""
                    }`}
                  >
                    <div className="p-5 flex items-start gap-4">
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 border ${
                          rule.enabled
                            ? "bg-[#E1F5EE] dark:bg-emerald-950/50 border-[#9FE1CB] dark:border-emerald-800"
                            : "bg-gray-50 dark:bg-slate-800 border-gray-200 dark:border-slate-700"
                        }`}
                      >
                        <Icon
                          className={`w-5 h-5 ${
                            rule.enabled
                              ? "text-[#0F6E56] dark:text-emerald-400"
                              : "text-gray-400 dark:text-gray-500"
                          }`}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1 gap-3">
                          <h3 className={`font-bold ${workflowTheme.textPrimary}`}>{meta.title}</h3>
                          <Switch
                            checked={rule.enabled}
                            onCheckedChange={() => toggleAutomation(meta.type)}
                          />
                        </div>
                        <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded bg-[#FFFBEB] dark:bg-amber-950/40 text-[#B45309] dark:text-amber-300 border border-[#FEF3C7] dark:border-amber-900/50 text-[11px] font-medium tracking-wide mb-3">
                          <Zap className="w-3 h-3" />
                          {meta.description}
                        </div>

                        {!isExpanded ? (
                          <div className={`flex items-center justify-between mt-4 p-3 rounded-lg border ${workflowTheme.surfaceMuted} ${workflowTheme.borderSubtle}`}>
                            <p className={`text-sm ${workflowTheme.textSecondary} truncate max-w-xl italic`}>
                              &quot;{message}&quot;
                            </p>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 text-[#0F6E56] dark:text-emerald-400 shrink-0"
                              onClick={() => setExpandedCard(meta.type)}
                            >
                              Edit Message
                            </Button>
                          </div>
                        ) : (
                          <div className={`mt-4 border rounded-lg p-4 ${workflowTheme.card}`}>
                            <label className={`text-xs font-bold ${workflowTheme.textPrimary} uppercase tracking-wider mb-2 block`}>
                              Message Content
                            </label>
                            <Textarea
                              className={`min-h-[100px] mb-3 ${workflowTheme.input}`}
                              value={message}
                              onChange={(e) => updateMessage(meta.type, e.target.value)}
                            />
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setExpandedCard(null)}
                              >
                                Close
                              </Button>
                              <Button
                                size="sm"
                                className="bg-[hsl(158,48%,36%)] hover:bg-[hsl(158,48%,30%)] text-white font-semibold"
                                onClick={() => setExpandedCard(null)}
                              >
                                <Check className="w-4 h-4 mr-1" /> Done
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </>
        )}
    </div>
  );

  if (embedded) {
    return panelContent;
  }

  return (
    <div className={waShell(whatsappPageShell.focused, "animate-fade-in")}>
      {panelContent}
    </div>
  );
}

export default function BasicAutomations() {
  const location = useLocation();
  const createSuffix = location.state?.create ? "&create=1" : "";

  return <Navigate to={`/whatsapp/workflows?tab=basic-automations${createSuffix}`} replace />;
}
